import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const LittleSaigonRealEstatePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&county=Orange&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Little Saigon properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Little Saigon Real Estate | Vietnamese Community Homes | Truc Doan</title>
        <meta name="description" content="Explore Little Saigon real estate in Westminster CA. Find homes near Vietnamese businesses, restaurants, and cultural centers. Truc Doan, your bilingual realtor." />
        <meta name="keywords" content="Little Saigon real estate, Little Saigon homes for sale, Vietnamese community homes, Westminster Little Saigon, buy home Little Saigon, Vietnamese realtor Orange County, Bolsa Avenue homes, Truc Doan Little Saigon, Asian Garden Mall homes" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/little-saigon-real-estate`} />
        <meta property="og:title" content="Little Saigon Real Estate | Vietnamese Community Homes | Truc Doan" />
        <meta property="og:description" content="Find homes in Little Saigon, the heart of Vietnamese-American culture. Browse listings near Bolsa Avenue, Asian Garden Mall, and more." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/little-saigon-real-estate`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Little Saigon, Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": "+1-818-818-0989",
            "email": "doanestate@gmail.com",
            "url": `${SEO_CONFIG.DOMAIN}/little-saigon-real-estate`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan is a bilingual Vietnamese-English real estate agent specializing in Little Saigon and Westminster CA properties. Deep community knowledge and cultural expertise.",
            "areaServed": {
              "@type": "Place",
              "name": "Little Saigon, Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Little Saigon Real Estate</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Find your home in the heart of America's largest Vietnamese-American community — Little Saigon, Westminster CA.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Buy a Home in Little Saigon?</h2>
          <p>Little Saigon is more than a neighborhood — it's a thriving cultural district that spans Westminster and parts of Garden Grove, representing the largest concentration of Vietnamese-Americans in the world outside of Vietnam. Living in Little Saigon means having world-class Vietnamese cuisine, specialty grocery stores, traditional medicine practitioners, and vibrant cultural celebrations right at your doorstep.</p>
          <p>The area offers a unique lifestyle where East meets West, with modern American conveniences alongside authentic Vietnamese traditions. From Tết (Lunar New Year) festivals to weekend farmers markets, from phở restaurants open late into the night to peaceful Buddhist temples, Little Saigon is a community that celebrates its heritage while embracing its future. Homebuyers here enjoy strong property values driven by consistent demand from families who value cultural connection.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Little Saigon Culture & Amenities</h2>
          <p>Little Saigon's Bolsa Avenue corridor is the cultural and commercial backbone of the Vietnamese-American community in Orange County.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Asian Garden Mall (Phước Lộc Thọ)</h3>
              <p>The iconic center of Little Saigon with hundreds of shops, restaurants, jewelers, and cultural vendors. A landmark for the Vietnamese-American community.</p>
            </div>
            <div className="amenity">
              <h3>Bolsa Avenue Corridor</h3>
              <p>Miles of authentic Vietnamese restaurants, bakeries, and specialty shops. From phở and bánh mì to chè and bò kho — a food lover's paradise.</p>
            </div>
            <div className="amenity">
              <h3>Vietnamese Supermarkets</h3>
              <p>ABC Supermarket, Lee's Sandwiches, and numerous specialty markets offer fresh produce, imported goods, and authentic ingredients.</p>
            </div>
            <div className="amenity">
              <h3>Cultural Centers & Temples</h3>
              <p>Buddhist temples, community centers, and cultural organizations maintain Vietnamese heritage and offer community gatherings year-round.</p>
            </div>
            <div className="amenity">
              <h3>Tết Festival & Events</h3>
              <p>The annual Tết Festival attracts over 100,000 visitors. Year-round cultural events, music performances, and community celebrations.</p>
            </div>
            <div className="amenity">
              <h3>Healthcare & Services</h3>
              <p>Vietnamese-speaking doctors, dentists, pharmacies, and professional services make daily life convenient for Vietnamese-speaking residents.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Little Saigon Real Estate Market Snapshot</h2>
          <p>Homes near Little Saigon benefit from strong demand driven by buyers seeking cultural community connection and walkable access to Vietnamese amenities.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Median Home Price (Little Saigon Area)</td>
                <td>~$720,000</td>
              </tr>
              <tr>
                <td>Average Days on Market</td>
                <td>18–28 Days</td>
              </tr>
              <tr>
                <td>Most Popular Price Range</td>
                <td>$500K – $850K</td>
              </tr>
              <tr>
                <td>Property Types</td>
                <td>Single Family, Condos, Multi-Family</td>
              </tr>
              <tr>
                <td>Buyer Demographics</td>
                <td>Families, Investors, First-Time Buyers</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Current Homes for Sale Near Little Saigon</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              Browse all available Little Saigon area listings by clicking "Search Listings" below.
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Buy or Sell in Little Saigon?</h2>
          <p>Contact Truc Doan today for a free consultation. As a bilingual Vietnamese-English agent, I understand the unique needs of Little Saigon homebuyers and sellers.</p>
          <div className="cta-buttons">
            <a href="mailto:doanestate@gmail.com?subject=Little Saigon Real Estate Inquiry" className="cta-button primary">
              Email Truc Doan
            </a>
            <a href="tel:+1-818-818-0989" className="cta-button secondary">
              Call +1-818-818-0989
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              Search Little Saigon Listings
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default LittleSaigonRealEstatePage;
